package com.dynamicruntime.common.util

fun String.splitComma(): List<String> {
    if (this.isBlank()) return emptyList()
    return this.split(",").map {it.trim()}
}

/**
 * Whether this string is a legal variable name: non-empty, starting with a letter or underscore and
 * otherwise made of letters, digits, and underscores. Written from scratch (rather than porting dn's
 * JVM-only `isJavaName`) so it is KMP compatible -- `Char.isLetter`/`isLetterOrDigit` are common stdlib.
 */
fun String.isVariableName(): Boolean =
    isNotEmpty() && (this[0].isLetter() || this[0] == '_') && isPlainName("_")

/**
 * Whether this string is a **plain name** (issue #1275): non-empty, and made only of letters, digits, and the
 * characters in [extra] -- starting with a letter when [letterFirst], and with only ASCII letters and digits when
 * [asciiOnly]. The one way the codebase checks that a value is "a name of letters and digits plus a few marks", so
 * each such check states its own marks, first character, and alphabet, and nothing else.
 *
 * **Letters are Unicode by default**, as [Char.isLetterOrDigit] has them: `é` and Cyrillic letters pass. Say
 * [asciiOnly] where the name goes somewhere a lookalike or an unusual character matters -- a command line, a URL
 * segment, an id compared by people.
 *
 * Written as a character check rather than a regex, as [isVariableName] and [isEmailAddress] are: it is the same in
 * the browser as on the JVM, and it cannot backtrack. A length rule, or a structure such as an address's one `@`, is
 * the caller's, beside the call.
 */
fun String.isPlainName(extra: String, letterFirst: Boolean = false, asciiOnly: Boolean = false): Boolean {
    if (isEmpty()) return false
    val first = this[0]
    if (letterFirst && !(if (asciiOnly) first.isAsciiLetter() else first.isLetter())) return false
    return all { (if (asciiOnly) it.isAsciiLetterOrDigit() else it.isLetterOrDigit()) || it in extra }
}

/** Whether this is an ASCII letter: `a`-`z` or `A`-`Z`, unlike [Char.isLetter], which takes any alphabet's. */
fun Char.isAsciiLetter(): Boolean = this in 'a'..'z' || this in 'A'..'Z'

/** Whether this is an ASCII letter or digit, unlike [Char.isLetterOrDigit], which takes any alphabet's. */
fun Char.isAsciiLetterOrDigit(): Boolean = isAsciiLetter() || this in '0'..'9'

/**
 * Whether this string is a plausibly valid email address. Pragmatic, **not** full RFC 5322: the goal is to
 * turn away the obvious non-address -- a bare username (`test_august`), a value with spaces, a missing or
 * dotless domain -- without rejecting the unusual-but-legal. So it is lenient on the local part and strict on
 * the shape everyone agrees on: exactly one `@`, a non-empty local part, and a domain of dot-separated labels
 * ending in a letters-only TLD.
 *
 * Written by hand (over a catch-all regex) for two reasons this codebase cares about: it compiles to the JVM
 * *and* the browser from `base/kernel`, so the admin create form and the endpoint that backs it agree on what
 * an address is; and a single forward scan cannot catastrophically backtrack the way an email regex famously
 * can. Length caps follow the RFC (local <= 64, whole address <= 254). The address is checked as given -- a
 * caller wanting to accept surrounding whitespace should [trim] first.
 */
fun String.isEmailAddress(): Boolean {
    val at = indexOf('@')
    // Exactly one '@', with something on each side of it.
    if (at <= 0 || at != lastIndexOf('@') || at == length - 1) return false
    if (length > 254 || any { it.isWhitespace() }) return false
    return isEmailLocalPart(substring(0, at)) && isEmailDomainPart(substring(at + 1))
}

/** The local part: non-empty, <= 64 chars, from a permissive set, no leading/trailing or doubled dot. */
private fun isEmailLocalPart(local: String): Boolean {
    if (local.isEmpty() || local.length > 64) return false
    if (local.first() == '.' || local.last() == '.') return false
    var prevDot = false
    for (ch in local) {
        val ok = ch in 'a'..'z' || ch in 'A'..'Z' || ch in '0'..'9' || ch in emailLocalExtra
        if (!ok) return false
        prevDot = if (ch == '.') { if (prevDot) return false; true } else false
    }
    return true
}

/** The domain part: dot-separated LDH labels (so at least one dot), ending in an alphabetic TLD of 2+ chars. */
private fun isEmailDomainPart(domain: String): Boolean {
    if (domain.length > 253) return false
    val labels = domain.split('.')
    // At least two labels: an address must name a domain, not a bare host (`user@localhost` is not one here).
    if (labels.size < 2) return false
    for (label in labels) {
        if (label.isEmpty() || label.length > 63) return false
        if (label.first() == '-' || label.last() == '-') return false
        if (!label.all { it in 'a'..'z' || it in 'A'..'Z' || it in '0'..'9' || it == '-' }) return false
    }
    val tld = labels.last()
    return tld.length >= 2 && tld.all { it in 'a'..'z' || it in 'A'..'Z' }
}

/** The characters an email local part may carry beyond letters and digits (the RFC's `atext`, minus quoting). */
private const val emailLocalExtra = ".!#$%&'*+/=?^_`{|}~-"

/**
 * An email address in the one form it is stored and looked up in (issue #743): surrounding whitespace dropped
 * and the **whole** address lowercased. The RFC lets a local part be case-sensitive; no mail provider honors
 * that, and a login system that did would make `Ada@Example.com` and `ada@example.com` two people. Applied at
 * every entry point -- registration, login, Google sign-in, the admin create form, the fixture -- *before*
 * [isEmailAddress] and before any lookup, so that lookups can stay exact-match against the stored value
 * (`AuthUsers.primaryId` is a unique index on the normalized form). Idempotent, so normalizing twice is harmless.
 * In the kernel because the admin console runs it in the browser before its own check.
 */
/**
 * This string with surrounding whitespace stripped, or null when it is null or becomes empty -- so a
 * whitespace-only value reads as "no value". The one operation the trim-and-blank-check helpers share: a bare
 * `String?` normalizer (`AuthUserRow.normalizeName`) and the string half of the map accessors
 * `getReqNonBlankStr` / `getOptNonBlankStr` both delegate here.
 */
fun String?.trimToNull(): String? = this?.trim()?.ifEmpty { null }

fun String.normalizeEmail(): String = trim().lowercase()

/**
 * A login id in the form it is looked up in (issue #743): an email address ([normalizeEmail]) when it carries
 * an `@`, else a **username**, only trimmed -- a username has its own rules and is never an address (the two
 * spaces are disjoint, which is what lets `queryByLoginId` try one then the other).
 */
fun String.normalizeLoginId(): String = if (contains('@')) normalizeEmail() else trim()

/**
 * Converts a camelCase identifier to lower_snake_case (e.g. `myField` -> `my_field`) by inserting an
 * underscore before each interior uppercase letter and lowercasing throughout. Used to turn code-side field
 * names into database column/table names when the target database does not preserve the case of the identifier.
 */
fun String.toLowerCaseIdentifier(): String {
    val sb = StringBuilder(length + 4)
    for ((i, ch) in this.withIndex()) {
        if (ch.isUpperCase()) {
            if (i > 0) sb.append('_')
            sb.append(ch.lowercaseChar())
        } else {
            sb.append(ch)
        }
    }
    return sb.toString()
}

/** Upper-cases the first character, leaving the rest unchanged (empty string passes through). */
fun String.capitalizeFirst(): String =
    if (isEmpty()) this else this[0].uppercaseChar() + substring(1)

/** Default cap for [sanitizeForDisplay]. */
const val defaultDisplayLen = 120

/**
 * Makes an untrusted string safe to display in the frontend -- including when the frontend renders it as
 * Markdown (issue #108). Used on user-supplied values substituted into error-message templates, so a value
 * like `[click](http://evil)` cannot become a link a user might trust.
 *
 * Three passes: (1) every run of whitespace (newlines and tabs included) collapses to a single space, trimmed
 * -- so a multi-line value becomes one tidy line rather than injecting breaks; (2) the characters that
 * structure Markdown links, images, autolinks, and code spans (`[ ] ( ) < > \``) are removed -- this keeps the
 * text but defuses the URL constructs; `*` and `_` are deliberately kept, since they only emphasize and are
 * common in real emails/usernames; (3) longer than [maxLen] is clipped with a trailing `…`.
 */
fun String.sanitizeForDisplay(maxLen: Int = defaultDisplayLen): String {
    val collapsed = trim().replace(whitespaceRun, " ").filterNot { it in markdownStructural }
    return if (collapsed.length <= maxLen) collapsed else collapsed.take(maxLen - 1).trimEnd() + "…"
}

private val whitespaceRun = Regex("\\s+")
private const val markdownStructural = "[]()<>`"

// Creating a format that defines options for both byte arrays and numeric values.
val customHexFormat = HexFormat {
    upperCase = true
}

fun String.encodeLiteral(): String  =
    StringBuilder(this.length + 10).appendLiteral(this).toString()

fun StringBuilder.appendLiteral(text: String): StringBuilder {
    for (ch in text) {
        when (ch) {
            '"' -> append("\\\"")
            '\\' -> append("\\\\")
            '\b' -> append("\\b")
            '\n' -> append("\\n")
            '\r' -> append("\\r")
            '\u000c' -> append("\\f")
            '\t' -> append("\\t")
            else -> {
                //Reference: http://www.unicode.org/versions/Unicode5.1.0/
                if ((ch <= '\u001F') || (ch in '\u007F'..'\u009F') || (ch in '\u2000'..'\u20FF')) {
                    append("\\u")
                    append(ch.toUpperHex())
                } else {
                    append(ch)
                }
            }
        }
    }
    return this
}

/** Formats to four characters of uppercase hex */
fun Char.toUpperHex(): String =
    this.code.toShort().toHexString(customHexFormat)

/**
 * A short, readable label from a wire key: `expenseReport` -> `Expense report`, `perItemAmount` -> `Per item
 * amount`. A space goes in before each capital and the result reads as one sentence-cased phrase.
 *
 * The fallback when a field declares no `title`. Deliberately simple -- a nicety over the key, not a
 * translation -- so a run of capitals (an acronym) spaces out oddly; a field that cares declares a title.
 * In the kernel so the backend names a trait in a message the way the pages head it (the lock guard, say). Pure,
 * and covered under the webapp's `jsNodeTest`.
 */
fun humanizeFieldName(name: String): String {
    if (name.isEmpty()) return name
    val sb = StringBuilder()
    name.forEachIndexed { i, c ->
        if (i > 0 && c.isUpperCase()) sb.append(' ')
        sb.append(c.lowercaseChar())
    }
    return sb.toString().replaceFirstChar { it.uppercaseChar() }
}

/**
 * [items] as one phrase for a sentence: `A`, `A and B`, `A, B and C` -- how a message names several things (the
 * traits a lock holds, the workflows holding them). Empty for none.
 */
fun joinAsPhrase(items: List<String>, last: String = "and"): String = when (items.size) {
    0 -> ""
    1 -> items[0]
    else -> items.dropLast(1).joinToString(", ") + " $last " + items.last()
}

private const val bytesPerKb = 1024L
private const val bytesPerMb = bytesPerKb * bytesPerKb

/**
 * A size as a person reads one: `640 bytes`, `12 KB`, `1 MB`, `1.5 MB`. **Rounded up**, so a file never reads as
 * smaller than it is -- which matters where the number sits beside a limit -- and a megabyte is said as one from
 * the first size that rounds to it, never as `1024 KB`.
 *
 * In the kernel so a refusal the backend words ("at most 256 KB; this file is 300 KB") and the page that listed
 * the limit say a size the same way.
 */
fun byteSizeText(bytes: Long): String {
    if (bytes < bytesPerKb) return if (bytes == 1L) "1 byte" else "$bytes bytes"
    val kb = (bytes + bytesPerKb - 1) / bytesPerKb
    if (kb < bytesPerKb) return "$kb KB"
    val tenths = (bytes * 10 + bytesPerMb - 1) / bytesPerMb
    return if (tenths % 10 == 0L) "${tenths / 10} MB" else "${tenths / 10}.${tenths % 10} MB"
}
